/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { getUserEvents } from "../../firebaseFunctions/getUserEvents/getUserEvents";
import {
  softDeleteEvent,
  cancelEvent,
  restoreEvent,
  permanentlyDeleteEvent,
} from "../../firebaseFunctions/updateEventStatus/updateEventStatus";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PebbleTable } from "../ui/TableComponents";
import ResponsiveTabs from "../ui/ResponsiveTabs";
import { formatEventDateRange } from "../../utils/dateAndTime";
import { useBreadcrumb } from "../../context/BreadCrumbContext";
import { useCreateEventContext } from "../../context/CreateEventContext";
import ModalLoadingState from "../PopUpModal/ModalLoadingState";
import picturePlaceholder from "../../assets/images/no_image_placeholder.jpg";
import {
  SearchIcon,
  CalendarIcon,
  PlusIcon,
  DotsVerticalIcon,
  RefreshIcon,
  TrashIcon,
  XCircleIcon,
  ExclamationIcon,
} from "@heroicons/react/outline";

// ─── Confirm Dialog ──────────────────────────────────────────────────────────

const ConfirmDialog = ({ title, message, warning, confirmLabel, confirmStyle, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onCancel} />
    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-4 ${
        confirmStyle === 'danger' ? 'bg-red-50' : 'bg-amber-50'
      }`}>
        <ExclamationIcon className={`w-5 h-5 ${
          confirmStyle === 'danger' ? 'text-red-500' : 'text-amber-500'
        }`} />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500 mb-2">{message}</p>
      {warning && (
        <p className="text-xs text-red-600 font-medium bg-red-50 rounded-lg px-3 py-2 mb-4">
          {warning}
        </p>
      )}
      <div className="flex gap-2 justify-end mt-4">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
            confirmStyle === 'danger'
              ? 'bg-red-600 text-white hover:bg-red-700'
              : 'bg-amber-500 text-white hover:bg-amber-600'
          }`}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// ─── Row 3-dot Actions Menu ───────────────────────────────────────────────────

const EventActionsMenu = ({ event, isDeleted, onDelete, onCancel, onRestore, onPermanentDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (isDeleted) {
    return (
      <div className="relative" ref={ref}>
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
        >
          <DotsVerticalIcon className="w-4 h-4" />
        </button>
        {open && (
          <div className="absolute right-0 bottom-full mb-1 z-20 bg-white border border-gray-200 rounded-xl shadow-lg py-1 w-44" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => { setOpen(false); onRestore(event); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
            >
              <RefreshIcon className="w-4 h-4 text-green-500" />
              Restore Event
            </button>
            <div className="my-1 border-t border-gray-100" />
            <button
              onClick={() => { setOpen(false); onPermanentDelete(event); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition"
            >
              <TrashIcon className="w-4 h-4" />
              Delete Forever
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
      >
        <DotsVerticalIcon className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 bottom-full mb-1 z-20 bg-white border border-gray-200 rounded-xl shadow-lg py-1 w-44" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => { setOpen(false); onCancel(event); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition"
          >
            <XCircleIcon className="w-4 h-4 text-amber-500" />
            Cancel Event
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button
            onClick={() => { setOpen(false); onDelete(event); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition"
          >
            <TrashIcon className="w-4 h-4" />
            Delete Event
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

function EventContents({ setActiveContent, setActiveStep, selectEvent }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { setBreadcrumb } = useBreadcrumb();
  const { resetEventData } = useCreateEventContext();
  const UserID = user.uid;
  const [userEvents, setUserEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'all';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmAction, setConfirmAction] = useState(null); // { type, event }
  const navigate = useNavigate();

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['all', 'upcoming', 'ongoing', 'archived', 'draft', 'deleted'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const tabs = [
    { label: t('common.all'), value: "all" },
    { label: t('common.upcoming'), value: "upcoming" },
    { label: t('common.ongoing'), value: "ongoing" },
    { label: t('common.archived'), value: "archived" },
    { label: t('common.draft'), value: "draft" },
    { label: "Deleted", value: "deleted" },
  ];

  const displayEvents = useCallback(async () => {
    setIsLoading(true);
    const response = await getUserEvents(UserID);
    if (Array.isArray(response)) {
      setUserEvents(response);
    } else {
      setUserEvents([]);
    }
    setIsLoading(false);
  }, [UserID]);

  const filterEvents = useCallback((events) => {
    const now = new Date();

    const filtered = events.filter((event) => {
      const loadIn = new Date(`${event.loadInDate}T${event.loadInTime || "00:00"}`);
      const loadOut = new Date(`${event.loadOutDate}T${event.loadOutTime || "23:59"}`);
      const isDeleted = event.status === "deleted";

      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          event.eventName?.toLowerCase().includes(query) ||
          event.eventLocation?.toLowerCase().includes(query) ||
          event.eventDescription?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      if (activeTab === "deleted") return isDeleted;
      if (isDeleted) return false; // hide deleted from all other tabs

      if (activeTab === "all") return !event.isDraft;
      if (activeTab === "upcoming") return !event.isDraft && loadIn > now;
      if (activeTab === "ongoing") return !event.isDraft && loadIn <= now && loadOut >= now;
      if (activeTab === "archived") return !event.isDraft && loadOut < now;
      if (activeTab === "draft") return event.isDraft === true;

      return false;
    });

    setFilteredEvents(filtered);
  }, [activeTab, searchQuery]);

  useEffect(() => {
    filterEvents(userEvents);
  }, [filterEvents, userEvents]);

  useEffect(() => {
    displayEvents();
  }, [displayEvents]);

  // ── Action handlers ──

  const handleDeleteEvent = (event) => {
    setConfirmAction({ type: 'delete', event });
  };

  const handleCancelEvent = (event) => {
    setConfirmAction({ type: 'cancel', event });
  };

  const handleRestoreEvent = (event) => {
    setConfirmAction({ type: 'restore', event });
  };

  const handlePermanentDelete = (event) => {
    setConfirmAction({ type: 'permanent', event });
  };

  const executeAction = async () => {
    if (!confirmAction) return;
    const { type, event } = confirmAction;
    setConfirmAction(null);

    if (type === 'delete') {
      await softDeleteEvent(event.id);
    } else if (type === 'cancel') {
      await cancelEvent(event.id);
    } else if (type === 'restore') {
      await restoreEvent(event.id);
    } else if (type === 'permanent') {
      await permanentlyDeleteEvent(event.id);
    }

    await displayEvents();
  };

  // ── Table data ──

  const mapUserEventsToTableData = (events) => {
    const now = new Date();
    const isDeletedTab = activeTab === "deleted";

    return events.map((event) => {
      const startTime = event.startTime || "00:00";
      const endTime = event.endTime || "00:00";
      const startDateTime = new Date(`${event.startDate}T${startTime}`);
      const endDateTime = new Date(`${event.endDate}T${endTime}`);

      let eventStatus = "";
      if (event.status === "deleted") {
        eventStatus = "Deleted";
      } else if (event.status === "cancelled") {
        eventStatus = "Cancelled";
      } else if (event.isDraft) {
        eventStatus = "Draft";
      } else if (startDateTime > now) {
        eventStatus = "Upcoming";
      } else if (startDateTime <= now && endDateTime >= now) {
        eventStatus = "Ongoing";
      } else if (endDateTime < now) {
        eventStatus = "Archived";
      }

      const approvedSellers = event?.mpsregistrations?.filter(
        (r) => r.applicationStatus === "Registered"
      );

      return {
        id: event.id,
        image: event?.eventCoverPhoto || picturePlaceholder,
        eventName: event.eventName,
        eventStatus,
        grossRevenue: `$${event.transactionFees?.toFixed(2) || "0.00"}`,
        sellersAndPendingApproval: event?.mpsregistrations?.length || 0,
        registeredSellers: approvedSellers?.length || 0,
        action: isDeletedTab ? null : (event.isDraft ? "Edit" : "View"),
        date: formatEventDateRange(
          event.loadInDate,
          event.loadInTime || "00:00",
          event.loadOutDate,
          event.loadOutTime || "23:59"
        ),
        // pass raw event for menus
        _raw: event,
      };
    });
  };

  const handleCreateEventClick = () => {
    resetEventData();
    navigate("/eo/create-event");
  };

  const handleEditDraft = (draftEvent) => {
    navigate("/eo/create-event", { state: { existingEventData: draftEvent } });
  };

  const handleViewEvent = (id) => {
    if (activeTab === "deleted") return; // no navigation for deleted
    const event = userEvents.find((e) => e.id === id);
    if (event) {
      if (event.isDraft) {
        handleEditDraft(event);
      } else {
        setBreadcrumb({ eventName: event.eventName });
        navigate(`/eo/events/${id}`);
      }
    }
  };

  const isDeletedTab = activeTab === "deleted";

  const columns = [
    {
      header: "",
      key: "image",
      type: "image",
    },
    {
      header: t('events.eventName'),
      key: "eventName",
      type: "compound",
      primaryKey: "eventName",
      secondaryKey: "date",
      underlinePrimary: !isDeletedTab,
      secondaryStyles: "text-[12px] pt-1 whitespace-nowrap",
    },
    {
      header: t('events.eventStatus'),
      key: "eventStatus",
      type: "status",
    },
    {
      header: t('events.grossRevenue'),
      key: "grossRevenue",
      type: "status",
      statusType: "payment",
    },
    {
      header: t('events.sellersPendingApproval'),
      key: "sellersAndPendingApproval",
      type: "text",
    },
    {
      header: t('events.registeredSellers'),
      key: "registeredSellers",
      type: "text",
    },
    ...(!isDeletedTab ? [{
      header: "",
      key: "action",
      type: "button",
      onClick: (id) => handleViewEvent(id),
    }] : []),
    {
      header: "",
      key: "menu",
      render: (row) => (
        <EventActionsMenu
          event={row._raw}
          isDeleted={isDeletedTab}
          onDelete={handleDeleteEvent}
          onCancel={handleCancelEvent}
          onRestore={handleRestoreEvent}
          onPermanentDelete={handlePermanentDelete}
        />
      ),
    },
  ];

  const emptyStateMessages = {
    all: { title: t('ticketing.noEventsYet'), subtitle: t('ticketing.noEventsDesc') },
    upcoming: { title: t('events.noUpcomingEventsTitle'), subtitle: t('events.noUpcomingEventsDesc') },
    ongoing: { title: t('events.noOngoingEventsTitle'), subtitle: t('events.noOngoingEventsDesc') },
    archived: { title: t('events.noPastEventsTitle'), subtitle: t('events.noPastEventsDesc') },
    draft: { title: t('events.noDraftsTitle'), subtitle: t('events.noDraftsDesc') },
    deleted: { title: "No deleted events", subtitle: "Events you delete will appear here. You can restore them or remove them permanently." },
  };

  const currentEmptyState = emptyStateMessages[activeTab] || emptyStateMessages.all;

  // ── Confirm dialog config ──

  const confirmConfig = {
    delete: {
      title: "Delete this event?",
      message: `"${confirmAction?.event?.eventName}" will be moved to Deleted. You can restore it anytime.`,
      warning: null,
      confirmLabel: "Delete",
      confirmStyle: "danger",
    },
    cancel: {
      title: "Cancel this event?",
      message: `"${confirmAction?.event?.eventName}" will be marked as Cancelled. Registered vendors will start fresh — MPS fees are refunded.`,
      warning: "This cannot be undone. The event will remain visible with a Cancelled status.",
      confirmLabel: "Cancel Event",
      confirmStyle: "warning",
    },
    restore: {
      title: "Restore this event?",
      message: `"${confirmAction?.event?.eventName}" will be restored and become visible again.`,
      warning: null,
      confirmLabel: "Restore",
      confirmStyle: "warning",
    },
    permanent: {
      title: "Delete forever?",
      message: `"${confirmAction?.event?.eventName}" will be permanently removed. This cannot be undone.`,
      warning: "⚠ This action is permanent and cannot be reversed.",
      confirmLabel: "Delete Forever",
      confirmStyle: "danger",
    },
  };

  const currentConfirm = confirmAction ? confirmConfig[confirmAction.type] : null;

  return (
    <>
      {isLoading ? (
        <ModalLoadingState />
      ) : (
        <div className="p-6">
          <div className="flex flex-col space-y-4">
            {/* Header with tabs and search */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-200 pb-3 sm:border-0 sm:pb-0">
              <ResponsiveTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} variant="underline" />
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('events.searchEvents')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-full sm:w-56"
                />
              </div>
            </div>

            {/* Deleted tab banner */}
            {isDeletedTab && (
              <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                <TrashIcon className="w-4 h-4 shrink-0" />
                <span>Deleted events can be restored at any time. Permanently deleted events cannot be recovered.</span>
              </div>
            )}

            {/* Events content */}
            {filteredEvents.length > 0 ? (
              <PebbleTable
                columns={columns}
                data={mapUserEventsToTableData(filteredEvents)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  {isDeletedTab
                    ? <TrashIcon className="w-8 h-8 text-gray-400" />
                    : <CalendarIcon className="w-8 h-8 text-gray-400" />
                  }
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {currentEmptyState.title}
                </h3>
                <p className="text-sm text-gray-500 mb-6 text-center max-w-sm">
                  {currentEmptyState.subtitle}
                </p>
                {!isDeletedTab && (
                  <button
                    onClick={handleCreateEventClick}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#4CAF50] text-white text-sm font-medium rounded-lg hover:bg-[#43A047] transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    {t('events.createEvent')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm dialog */}
      {confirmAction && currentConfirm && (
        <ConfirmDialog
          title={currentConfirm.title}
          message={currentConfirm.message}
          warning={currentConfirm.warning}
          confirmLabel={currentConfirm.confirmLabel}
          confirmStyle={currentConfirm.confirmStyle}
          onConfirm={executeAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </>
  );
}

export default EventContents;
